import { DotsVertical, Download01, File02, Loading01 } from '@ethsign/icons';

import { Button, Dropdown } from '@ethsign/ui';
import { shortenWalletAddress } from '@ethsign/utils-web';

import classNames from 'classnames';
import { sortBy } from 'lodash-es';
import { useEffect, useState } from 'react';

import { NetworkTag } from '@/components';
import { ColorSignatureIcon, UserIcon } from '@/components/Icons';
import { DetailModal } from '@/components/Modal/DetailModal';
import { ContractItem } from '@/types';
import { WalletType } from '@/utils';
type Props = {
  onUserChange: (id: string) => void;
  users?: any[];
  onDownload: (contractId?: string) => void;
  viewMore?: () => void;
  hasMore?: boolean;
  total?: number;
  contract?: ContractItem;
};
export const SignersList = (props: Props) => {
  const { onUserChange, onDownload, hasMore, viewMore, contract } = props;
  const users = sortBy(
    props.users?.filter((item) => item.role === 'signer'),
    ['signingAt']
  ).reverse();

  const [currentUser, setCurrentUser] = useState<any>(users?.[0] || {});
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentContract, setCurrentContract] = useState<ContractItem | null>(null);

  useEffect(() => {
    onUserChange && onUserChange(currentUser.contractId);
  }, [currentUser]);
  useEffect(() => {
    setCurrentUser(users?.[0] || {});
  }, [JSON.stringify(users)]);
  const total = contract?.recipientsTotal;
  return (
    <div className="w-[352px] pt-[38px]  border-r border-gray-200 flex flex-col ">
      <div className=" flex flex-row justify-between items-center px-6">
        <div>
          <span className="text-lg font-bold text-gray-600">Signers</span>
          <span className="ml-2 px-2 pt-[2px] text-xs font-medium text-primary-700 bg-primary-50 rounded-2xl">
            {total}
          </span>
        </div>
        {total && total <= 100 ? (
          <div
            className="flex flex-row items-center"
            onClick={() => {
              onDownload();
            }}
          >
            <Download01 size={16} color="#475467" />
            <span className="cursor-pointer ml-1 text-sm font-medium text-gray-700">Download all</span>
          </div>
        ) : null}
      </div>
      <div className="mt-[18px] flex-1 h-1 overflow-auto">
        {total ? (
          users?.map((item, i) => {
            const isActive = currentUser.address === item.address;
            return (
              <div
                key={i}
                className={classNames(
                  'w-full flex flex-row relative px-6 py-5 border-b border-gray-200 cursor-pointer',
                  {
                    'bg-primary-50 border-primary-300': isActive
                  }
                )}
                onClick={() => {
                  setCurrentUser(item);
                }}
              >
                <UserIcon fill={currentUser.address === item.address ? '#FDB022' : '#9E80B5'} />
                <div className="ml-3 flex-1">
                  <div className="flex flex-row justify-between items-center">
                    <div className="text-sm font-medium text-gray-700">
                      {item.nickName || shortenWalletAddress(item.address, 'shorter')}
                    </div>
                    <div className="flex flex-row items-center">
                      <Dropdown
                        items={[
                          {
                            icon: <Download01 size={20} color="#101828"></Download01>,
                            label: 'Download Contract',
                            onClick: () => {
                              onDownload(item.contractId);
                            }
                          },
                          {
                            icon: <File02 size={20} color="#101828"></File02>,
                            label: 'Contract Details',
                            onClick: () => {
                              setCurrentContract({
                                ...contract,
                                txId: item.contract?.txId,
                                recipients: [item],
                                id: item.contractId
                              });
                              setShowModal(true);
                            }
                          }
                        ]}
                      >
                        <DotsVertical
                          color="#98A2B3"
                          size={16}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        ></DotsVertical>
                      </Dropdown>
                    </div>
                  </div>
                  <div className="text-xs font-normal text-gray-500 mt-2  flex flex-row justify-start items-center">
                    {shortenWalletAddress(item.address, 'normal')}
                    <span className="ml-2 align-middle font-medium">
                      <NetworkTag network={item.chainType as WalletType} />
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="flex flex-col items-center mt-11">
            <ColorSignatureIcon />
            <div className="text-md font-medium text-gray-900 mt-4">No signatures yet</div>
            <div className="text-sm font-normal text-gray-500 mt-1">No one has signed the contract yet.</div>
          </div>
        )}
        {hasMore && (
          <div className="w-full px-2">
            <Button
              onClick={async () => {
                if (loading) return;
                setLoading(true);
                if (viewMore) {
                  try {
                    await viewMore();
                    setLoading(false);
                  } catch (error) {
                    setLoading(false);
                  }
                }
              }}
              className="w-full mt-2"
              variant="outline"
            >
              {loading ? (
                <span className="flex flex-row justify-center items-center">
                  <Loading01 className="animate-spin" size={16} color="#344054" />
                  <span className="ml-2">Loading</span>
                </span>
              ) : (
                'View More'
              )}
            </Button>
          </div>
        )}
        <DetailModal
          contract={currentContract as ContractItem}
          open={showModal}
          onOpenChange={setShowModal}
        ></DetailModal>
      </div>
    </div>
  );
};
